import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface IHTTPRequestOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  context?: HttpContext;
  observe?: 'body';
  params?:
    | HttpParams
    | Record<
        string,
        string | number | boolean | ReadonlyArray<string | number | boolean>
      >;
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  transferCache?:
    | {
        includeHeaders?: string[];
      }
    | boolean;
      body?: any; 

}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  private buildHeaders(): Headers {
    const headers = new Headers();
    return headers;
  }

  /**
   * Performs a GET request.
   * @param url The endpoint URL.
   * @param paramsObj (Optional) An object containing query parameters.
   */
  public get<T>(url: string, paramsObj?: unknown): Promise<T> {
    console.log(url);

    let params = new HttpParams();
    if (paramsObj) {
      for (const key of Object.keys(paramsObj)) {
        params = params.append(key, paramsObj[key as keyof typeof paramsObj]);
      }
    }
    const headers = this.buildHeaders();
    const observable: Observable<T> = this.http
      .get<T>(url, {
        params,
        headers: new HttpHeaders(headers),
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
    return lastValueFrom(observable);
  }

  /**
   * Performs a POST request.
   * @param url The endpoint URL.
   * @param data The data payload to send.
   * @param headers (Optional) Custom HTTP headers.
   * @param options (Optional) Request options including headers and params.
   */
  public post<T>(url: string, data: unknown, params?: HttpParams): Promise<T> {
    const headers = this.buildHeaders();
    const options = headers ? { headers: new HttpHeaders(headers) } : {};
    const observable: Observable<T> = this.http
      .post<T>(url, data, { ...options, withCredentials: true, params })
      .pipe(catchError(this.handleError));

    return lastValueFrom(observable);
  }
  /**
   * Performs a POST request that expects a Blob response (e.g. file downloads).
   * @param url The endpoint URL.
   * @param data The request body.
   * @param params Optional query parameters.
   * @returns A promise resolving to a Blob (or type T if explicitly cast).
   */
  public postBlob<T>(
    url: string,
    data: unknown,
    params?: HttpParams,
    passedHeaders?: Headers,
    options?: IHTTPRequestOptions
  ): Promise<T> {
    // merge both headers
    const headers = passedHeaders ?? this.buildHeaders();

    const httpRequestOptions: IHTTPRequestOptions = {
      ...options,
      headers: new HttpHeaders(headers),
      withCredentials: true,
      params: params,
    };

    const observable: Observable<T> = this.http
      .post<T>(url, data, httpRequestOptions)
      .pipe(catchError(this.handleError));

    return lastValueFrom(observable);
  }

  /**
   * Performs a PUT request.
   * @param url The endpoint URL.
   * @param data The data payload to update.
   * @param headers (Optional) Custom HTTP headers.
   */
  public put<T>(url: string, data: unknown, headers?: HttpHeaders): Promise<T> {
    const options = headers ? { headers } : {};
    const observable: Observable<T> = this.http
      .put<T>(url, data, { ...options, withCredentials: true })
      .pipe(catchError(this.handleError));
    return lastValueFrom(observable);
  }

  /**
   * Performs a PATCH request.
   * @param url The endpoint URL.
   * @param data The data payload to patch.
   * @param headers (Optional) Custom HTTP headers.
   */
  public patch<T>(url: string, data: unknown, headers?: HttpHeaders): Promise<T> {
    const options = headers ? { headers } : {};
    const observable: Observable<T> = this.http
      .patch<T>(url, data, { ...options, withCredentials: true })
      .pipe(catchError(this.handleError));
    return lastValueFrom(observable);
  }

  /**
   * Performs a DELETE request.
   * @param url The endpoint URL.
   */
  public delete<T>(url: string, headers?: HttpHeaders): Promise<T> {
    const options = headers ? { headers } : {};
    const observable: Observable<T> = this.http
      .delete<T>(url, { ...options, withCredentials: true })
      .pipe(catchError(this.handleError));
    return lastValueFrom(observable);
  }

    /**
   * Performs a DELETE request with a body.
   * @param url The endpoint URL.
   * @param body The data payload to send in the request body.
   * @param headers (Optional) Custom HTTP headers.
   */
  public deleteWithBody<T>(url: string, body: any, headers?: HttpHeaders): Promise<T> {
    const options: IHTTPRequestOptions = {
      headers: headers || new HttpHeaders(this.buildHeaders()),
      withCredentials: true,
      body: body // Include the body in the request options
    };

    const observable: Observable<T> = this.http
      .request<T>('DELETE', url, options)
      .pipe(catchError(this.handleError));
    
    return lastValueFrom(observable);
  }

  
  /**
   * A simple error handler: logs the error and rethrows it.
   * @param error The error response object.
   */
  private handleError(error: unknown): Observable<never> {
    // Log the error here if needed
    console.error('API Promise Service error:', error);
    return throwError(() => error);
  }
}
